import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback, useMemo, useRef } from "react";
import { usePrivy } from "@privy-io/react-auth";
import { FloatingLeftButton } from "@/components/FloatingLeftButton";
import { useGachaHistory, useStats } from "@/lib/hooks/useApi";
import type { GachaHistoryItem } from "@/lib/types/api";
import AnimatedLoading from "@/components/AnimatedLoading";
import AnimatedWaiting from "@/components/AnimatedWaiting";
import { DefaultButton } from "@/components/DefaultButton";
import { AvatarBorder } from "@/components/AvatarBorder";
import { UserName } from "@/components/UserName";
import { cn, generateTextShadow } from "@/lib/utils";
import { ItemIcon } from "@/routes/(game)/shop/-components/ItemIcon";
import {
  translateInventoryItemName,
  translateRarityName,
} from "@/lib/inventoryTranslations";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";

dayjs.extend(relativeTime);

export const Route = createFileRoute("/(game)/gacha/history")({
  component: GachaHistoryRoute,
});

function GachaHistoryRoute() {
  const navigate = useNavigate();
  const { user } = usePrivy();
  const walletAddress = user?.wallet?.address ?? "";
  const { data: statsData } = useStats(walletAddress);
  const gameWalletAddress = statsData?.embeddedWallet ?? "";

  const historyQuery = useGachaHistory();
  console.log("historyQuery", historyQuery);

  const listContainerRef = useRef<HTMLDivElement | null>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);

  const historyItems: GachaHistoryItem[] = useMemo(() => {
    if (!historyQuery.data?.pages) return [];

    const allItems = historyQuery.data.pages.flatMap((page) =>
      Array.isArray(page.data) ? page.data : [],
    );

    return allItems;
  }, [historyQuery.data?.pages]);

  // Intersection observer for infinite scroll
  const lastElementRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (historyQuery.isFetchingNextPage) return;
      if (observerRef.current) observerRef.current.disconnect();

      observerRef.current = new IntersectionObserver((entries) => {
        if (entries[0].isIntersecting && historyQuery.hasNextPage) {
          historyQuery.fetchNextPage();
        }
      });

      if (node) observerRef.current.observe(node);
    },
    [historyQuery],
  );

  if (historyQuery.isLoading) {
    return <AnimatedLoading />;
  }

  if (historyQuery.error) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center gap-3 bg-[#1D2A52]">
        <p className="text-white font-semibold text-center">
          Could not load Gacha history
        </p>
        <div className="flex flex-col items-center justify-center gap-2">
          <button
            onClick={() => historyQuery.refetch()}
            className="mt-4 px-4 py-2 rounded-md bg-white/10 text-white text-sm hover:bg-white/20 transition"
          >
            Try Again
          </button>
          <button className="text-white/70 text-xs">
            or{" "}
            <span
              className="text-blue-400 hover:text-blue-500 hover:cursor-pointer hover:underline active:scale-95 transition"
              onClick={() => navigate({ to: "/gacha" })}
            >
              Go to Gacha
            </span>{" "}
            while we fix this issue
          </button>
        </div>
      </div>
    );
  }

  if (!historyItems.length) {
    return (
      <div
        className="flex-1 min-h-0 flex flex-col items-center justify-center gap-4 relative"
        style={{
          backgroundImage:
            "url('https://cdn.navalabs.io/assets/bg-live-tournament_JeXUeg.png')",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
        }}
      >
        <div className="w-full flex justify-between items-center absolute left-0 top-4 z-50">
          <FloatingLeftButton
            onClick={() => navigate({ to: "/gacha" })}
            icon={
              <svg
                className="w-4 h-4"
                viewBox="0 0 9 8"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
                  fill="white"
                />
              </svg>
            }
          />
        </div>
        <div className="flex flex-col items-center justify-center gap-3">
          <img
            src="https://cdn.navalabs.io/icons/gacha-icon_Gf5APY.png"
            alt="Gacha"
            className="h-16 w-16 object-contain"
            style={{ imageRendering: "pixelated" }}
          />
          <div className="flex flex-col items-center justify-center">
            <p className="text-xl text-white font-semibold">
              No Gacha History Yet
            </p>
            <p className="text-white/70 text-xs text-center mx-4">
              Be the first to pull from Gacha!
            </p>
          </div>
          <DefaultButton
            onClick={() => navigate({ to: "/gacha" })}
            className="mt-4 px-4 py-2"
          >
            Go to Gacha
          </DefaultButton>
        </div>
      </div>
    );
  }

  return (
    <div
      className="flex-1 min-h-0 flex flex-col gap-4 relative"
      style={{
        backgroundImage:
          "url('https://cdn.navalabs.io/assets/bg-live-tournament_JeXUeg.png')",
        backgroundSize: "cover",
        backgroundPosition: "center",
        backgroundRepeat: "no-repeat",
      }}
    >
      <div className="w-full flex justify-between items-center absolute left-0 top-4 z-50">
        <FloatingLeftButton
          onClick={() => navigate({ to: "/gacha" })}
          icon={
            <svg
              className="w-4 h-4"
              viewBox="0 0 9 8"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M3 1V0H2V1H1V2H0V3H1V4H2V5H3V4H6V7H3V8H7V7H8V6H9V3H8V2H7V1H3Z"
                fill="white"
              />
            </svg>
          }
        />
      </div>

      <div className="mt-16 px-2 flex flex-col w-full items-center justify-center gap-1">
        <h2 className="text-3xl font-bold text-white uppercase text-center text-wrap">
          Gacha History
        </h2>
      </div>

      <div
        ref={listContainerRef}
        className={cn(
          "space-y-3 bg-[#1D2A52] px-3 pb-3",
          "border-t-4 border-[#0659B5]",
          "flex-1 min-h-0 overflow-y-auto py-3",
        )}
      >
        {historyItems.map((item, index) => {
          const isLastElement = index === historyItems.length - 1;
          const isCurrentUser = item.userAddress === gameWalletAddress;

          return (
            <div
              key={`${item.txSignature}-${index}`}
              ref={isLastElement ? lastElementRef : undefined}
              className={cn(
                "dark-blue-container relative p-3 transition-all max-w-2xl",
                isCurrentUser && "ring-2 ring-emerald-400/50",
              )}
            >
              <div className="flex gap-3">
                {/* User Avatar */}
                <div className="shrink-0">
                  <AvatarBorder
                    src={item.profilePicture}
                    decoration={item.activeFrameUrl}
                    size={48}
                  />
                </div>

                {/* Content */}
                <div className="flex-1 min-w-0">
                  {/* User info and time */}
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <UserName
                      username={item.username}
                      className={cn(
                        "text-sm font-bold truncate",
                        isCurrentUser ? "text-emerald-400" : "text-white",
                      )}
                      iconSize="w-3 h-3"
                      isVerified={item.isVerified}
                      shouldWarnUnverified={true}
                      domainType={item.domainType}
                    />
                    <span className="text-white/50 text-xs whitespace-nowrap">
                      {dayjs(item.claimedAt).fromNow()}
                    </span>
                  </div>

                  {/* Item pulled */}
                  <div className="flex items-center gap-3 bg-black/20 rounded-lg p-2">
                    <div className="shrink-0 w-12 h-12 flex items-center justify-center">
                      <ItemIcon
                        item={item}
                        className="w-10 h-10 object-contain"
                        userProfilePicture={item.profilePicture}
                      />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p
                        className="text-white text-sm font-bold truncate"
                        style={{ textShadow: generateTextShadow(0.8) }}
                      >
                        {translateInventoryItemName(
                          item.itemName,
                          undefined,
                          item.itemId,
                        )}
                      </p>
                      <p className="text-xs font-semibold uppercase text-gray-400">
                        {translateRarityName(item.rarity)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          );
        })}

        {historyQuery.isFetchingNextPage && (
          <AnimatedWaiting
            prefix=""
            className="w-full text-center text-lg py-4"
          />
        )}

        {!historyQuery.hasNextPage && historyItems.length > 0 && (
          <div className="py-4 text-center text-xs text-white/60">
            End of history
          </div>
        )}
      </div>
    </div>
  );
}
